import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
}

interface ImageUploadSimpleProps {
  images: ImageFile[];
  onImagesChange: (images: ImageFile[]) => void;
  maxImages?: number;
  maxSizeInMB?: number;
}

const ImageUploadSimple: React.FC<ImageUploadSimpleProps> = ({ 
  images, 
  onImagesChange, 
  maxImages = 5,
  maxSizeInMB = 5
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const processFiles = async (files: FileList) => {
    const validFiles: ImageFile[] = [];
    const errors: string[] = [];

    setIsUploading(true);

    for (const file of Array.from(files)) {
      if (images.length + validFiles.length >= maxImages) {
        errors.push(`Maximum ${maxImages} foto's toegestaan`);
        break;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Alleen afbeeldingen zijn toegestaan`);
        continue;
      }

      // Validate file size
      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > maxSizeInMB) {
        errors.push(`${file.name}: Te groot (${sizeInMB.toFixed(1)}MB). Maximum ${maxSizeInMB}MB`);
        continue;
      }

      try {
        // Create preview
        const preview = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        validFiles.push({
          id: crypto.randomUUID(),
          file,
          preview,
          name: file.name,
          size: file.size
        });
      } catch (error) {
        errors.push(`Fout bij verwerken van ${file.name}`);
      }
    }

    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
    }

    if (validFiles.length > 0) {
      onImagesChange([...images, ...validFiles]);
      toast.success(`${validFiles.length} foto${validFiles.length === 1 ? '' : '\'s'} toegevoegd!`);
    }

    setIsUploading(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const removeImage = (id: string) => {
    const updatedImages = images.filter(image => image.id !== id);
    onImagesChange(updatedImages);
    toast.success('Foto verwijderd');
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200
          ${isDragOver 
            ? 'border-navy-400 bg-navy-50' 
            : 'border-cream-300 hover:border-navy-300 hover:bg-cream-50'
          }
          ${isUploading ? 'opacity-50 pointer-events-none' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {isUploading ? (
          <div className="space-y-3">
            <div className="h-8 w-8 mx-auto border-2 border-navy-300 border-r-navy-600 rounded-full animate-spin" />
            <p className="text-navy-600">Foto's uploaden...</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Upload className="h-8 w-8 text-navy-400 mx-auto" />
            <div>
              <p className="text-navy-600 mb-1">
                Sleep je foto's hier of{' '}
                <button 
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-navy-600 font-semibold hover:text-navy-700 underline"
                >
                  klik om te uploaden
                </button>
              </p>
              <p className="text-sm text-navy-500">
                JPG, PNG, WebP tot {maxSizeInMB}MB • Maximum {maxImages} foto's
              </p>
              <p className="text-xs text-navy-400 mt-1">
                {images.length}/{maxImages} foto's toegevoegd
              </p>
            </div>
            {images.length < maxImages && (
              <Button 
                type="button" 
                variant="outline" 
                className="border-navy-300 text-navy-600 hover:bg-navy-50"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Selecteer Foto's
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-navy-800">
              Toegevoegde foto's ({images.length}/{maxImages})
            </h4>
            <p className="text-sm text-navy-600">
              Totale grootte: {formatFileSize(images.reduce((total, img) => total + img.size, 0))}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((image, index) => (
              <div 
                key={image.id}
                className="relative bg-white rounded-lg border-2 border-cream-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 group"
              >
                {/* Main Image Badge */}
                {index === 0 && (
                  <div className="absolute top-2 left-2 z-10">
                    <div className="bg-navy-600 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
                      Hoofdfoto
                    </div>
                  </div>
                )}

                {/* Image Preview */}
                <div className="aspect-square bg-cream-100 overflow-hidden">
                  <img
                    src={image.preview}
                    alt={image.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={() => removeImage(image.id)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-md opacity-0 group-hover:opacity-100 duration-200"
                  title="Verwijder foto"
                >
                  <X className="h-4 w-4" />
                </button>

                {/* File Info */}
                <div className="p-3 bg-white border-t-2 border-cream-100">
                  <p className="text-sm font-medium text-navy-800 truncate mb-1" title={image.name}>
                    {image.name}
                  </p>
                  <p className="text-xs text-navy-500">
                    {formatFileSize(image.size)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadSimple;